import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { config } from '@/config';
import { useCurrentUserContext } from '@/contexts';
import {
  resetPlannerOnboarding,
  usePlannerOnboardingEnabled,
} from '@/features/experiences/components/PlannerOnboarding';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { Box, Button, Link, useToast } from '@/ui';
import { PanelContent } from '../../components/PanelContent';
import { PanelHeader } from '../../components/PanelHeader';
import { SectionHeader } from '../../components/SectionHeader';
import { DeleteAccountDialog } from './DeleteAccount';

export const Help = () => {
  const { t } = useTranslation('settingsModal', { keyPrefix: 'help' });
  const { t: tOnboarding } = useTranslation('plannerOnboarding', {
    keyPrefix: 'help',
  });
  const toast = useToast();
  const { currentUser } = useCurrentUserContext();
  // Outside the rollout there is no guide to restart, and the confirmation
  // ("The guide will show on your next new planner") would be a promise the
  // flags will not keep.
  const { isEnabled: isOnboardingEnabled } = usePlannerOnboardingEnabled();
  const { value: isDeleteAccountEnabled } = useFeatureFlag(
    'deleteAccountEnabled'
  );
  const [isDeleteOpen, setDeleteOpen] = useState(false);

  const handleRestartGuide = () => {
    // Given no id the reset forgets every account this browser remembers, not
    // just this one, and the toast would promise a guide for all of them. With
    // identity unresolved there is nothing to restart, so hold still.
    if (!currentUser?.id) return;

    resetPlannerOnboarding(currentUser.id);
    toast.success(tOnboarding('restarted'));
  };

  return (
    <>
      <PanelHeader>{t('heading')}</PanelHeader>
      <PanelContent>
        <SectionHeader first>{t('documentation')}</SectionHeader>
        <Link href={config.helpUrl} target="_blank">
          {t('documentationLink')}
        </Link>

        <SectionHeader>{t('support')}</SectionHeader>
        <Link href={config.supportHref} target="_blank">
          {config.supportEmail}
        </Link>

        {isOnboardingEnabled && (
          <>
            <SectionHeader>{tOnboarding('heading')}</SectionHeader>
            <Button
              alignSelf="flex-start"
              size="ah-sm"
              variant="ahOutline"
              onClick={handleRestartGuide}
            >
              {tOnboarding('restart')}
            </Button>
          </>
        )}

        <SectionHeader>{t('version')}</SectionHeader>
        <Box color="customgray.mid" fontSize="small">
          {t('release', {
            version: config.version || 0,
            release: config.release,
          })}
        </Box>

        {isDeleteAccountEnabled && (
          <>
            <Button
              alignSelf="flex-start"
              color="destructive.500"
              fontWeight="normal"
              mt="8"
              variant="link"
              onClick={() => setDeleteOpen(true)}
            >
              {t('delete_account.link')}
            </Button>
            {isDeleteOpen && (
              <DeleteAccountDialog
                isOpen
                onClose={() => setDeleteOpen(false)}
              />
            )}
          </>
        )}
      </PanelContent>
    </>
  );
};
